import { setServicesApiRegion } from '@/services/api/deprecated';
import { setActiveAccount } from '@customerio/hydra-nav-state';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { getWorkspaceIdFromUrl, useCurrentWorkspace } from '../../context/current-workspace';
import { analytics } from '../analytics/client';
import { getMasqId } from '../../utils/masq';
import { getRoleDefinitions } from '@/features/roles/api';
import { augmentAccount, augmentUser } from './augment';
import { setAccountStore, setUserStore, setWorkspaceStore } from './store';
import type { Account, AugmentedAccount, AugmentedUser, RawAccount, RawUser, User, Workspace } from './types';
import { Region } from './types';
import { servicesApi } from '../api';
import { UserMasqPermission } from '@customerio/hydra-permissions/masq';

/**
 * Get auth token from cookies
 */

const MAX_RETRY_COUNT = 6;
const RETRYABLE_ERRORS = ['ERR_NETWORK', 'ERR_NETWORK_CHANGED'];

type Auth = {
  account: AugmentedAccount | null;
  user: AugmentedUser | null;
  workspaces: Workspace[] | null;
};

function getAuthMethod(
  workspaceId: string | null,
): () => Promise<{ account: Account | null; user: User | null; workspaces: Workspace[] | null }> {
  const currentUrl = window.location.href;

  const masqAccountId = getMasqId(currentUrl);
  if (masqAccountId != null && !Number.isNaN(Number.parseInt(masqAccountId))) {
    return async () => {
      return servicesApi
        .getAccountById({
          params: { accountId: Number.parseInt(masqAccountId) },
        })
        .then((response) => {
          return {
            account: getAccountFromRaw(response.account),
            user: getUserFromRaw(response.account, response.user),
            workspaces: response.environments,
          };
        });
    };
  }

  if (workspaceId != null && !Number.isNaN(Number.parseInt(workspaceId))) {
    return async () => {
      return servicesApi
        .getAccountByEnvironmentId({
          params: { environmentId: Number.parseInt(workspaceId) },
        })
        .then((response) => {
          return {
            account: getAccountFromRaw(response.account),
            user: getUserFromRaw(response.account, response.user),
            workspaces: response.environments,
          };
        });
    };
  }

  return async () => {
    return await servicesApi.getAccount().then((response) => {
      return {
        account: getAccountFromRaw(response.account),
        user: getUserFromRaw(response.account, response.user),
        workspaces: response.environments,
      };
    });
  };
}

function isAxiosError(error: unknown): error is AxiosError {
  return (error as { isAxiosError?: boolean })?.isAxiosError === true;
}

function isRetryableError(error: unknown): boolean {
  return isAxiosError(error) && RETRYABLE_ERRORS.includes(error.code ?? '');
}

/**
 * A query that checks if the user is authenticated and retrieves the access token.
 *
 * In most cases you want to use the `useAuth` hook instead of this query directly.
 */
export function useAuthQuery() {
  const currentWorkspace = useCurrentWorkspace();
  const queryClient = useQueryClient();
  const workspaceIdFromUrl = getWorkspaceIdFromUrl(window.location.href);
  const workspaceId = workspaceIdFromUrl ?? (currentWorkspace?.id != null ? String(currentWorkspace.id) : null);
  /**
   * Check if we are authenticated and get the access token
   */
  return useQuery({
    retry: (failureCount, error) => {
      // Retry on network errors
      if (isRetryableError(error) && failureCount < MAX_RETRY_COUNT) {
        return true;
      }
      return false;
    },
    queryKey: ['authenticated'],
    queryFn: async () => {
      try {
        const auth: Auth = { account: null, user: null, workspaces: null };
        const query = getAuthMethod(workspaceId);
        const result = await query();

        if (result.account != null) {
          if (result.account.is_sample_account_access && result.user != null) {
            const { account: a } = await servicesApi.getAccountById({
              params: { accountId: result.user.account_id },
            });
            auth.account = augmentAccount(getAccountFromRaw(a));
            setAccountStore(auth.account);
          } else {
            auth.account = augmentAccount(result.account);
            setAccountStore(auth.account);
          }
        }

        if (result.user != null) {
          auth.user = augmentUser(result.user);
          setUserStore(auth.user);
        }

        if (result.workspaces != null) {
          auth.workspaces = result.workspaces;
          setWorkspaceStore(auth.workspaces);
        }

        assignAuthInfoGlobal(auth);
        updateActiveAccount(auth);

        // Prefetch roles so role/permission data is available from one place.
        // Do not block auth on roles; populate the cache for `useRoleDefinitions` consumers.
        if (auth.account?.id != null) {
          void queryClient.prefetchQuery({
            queryKey: ['role_definitions', auth.account.id],
            queryFn: async () => {
              return await getRoleDefinitions(auth.account!.id);
            },
            staleTime: Infinity,
          });
        }

        return auth;
      } catch (error) {
        if (isRetryableError(error)) {
          // Allow retrying on network errors--throwing the error will trigger a retry attempt
          throw error;
        }
        return null;
      }
    },
  });
}

function updateActiveAccount(auth: Auth) {
  const regionId = auth.account?.region;
  let regionName: string | undefined;
  if (regionId != null) {
    regionName = regionId === Region.EU ? 'eu' : 'us';
  }
  setActiveAccount({
    id: auth.account?.id,
    regionId,
    regionName,
  });

  setServicesApiRegion(regionId ?? Region.US);
}

// Exposes auth info for external scripts like analytics trackers.
function assignAuthInfoGlobal(auth: Auth) {
  Object.assign(window, {
    __hydra__: {
      ...window.__hydra__,
      account: {
        id: auth.account?.id,
        dc: auth.account?.region === Region.EU ? 'eu' : 'us',
      },
      user: {
        id: auth.user?.id,
        email: auth.user?.email,
      },
      analytics,
    } satisfies Window['__hydra__'],
  });
}

// TODO: would be great if someone cleaned this up someday
function getAccountFromRaw(account: RawAccount): Account {
  return {
    id: +account.id,
    created_at: account.created_at,
    name: account.name,
    is_sample_account_access: account.is_sample_account_access,
    is_masq: account.is_masqueraded,
    write_access:
      !account.is_sample_account_access && !account.is_masqueraded && !account.read_only && !account.is_paused,
    feature_flags: account.feature_flags,
    logo_url: account.logo_url,
    premium: account.premium,
    premium_trial: account.premium_trial,
    plan_features: [],
    gated_features: account.gated_features,
    admin: account.admin,
    product_activations: account.product_activations ?? [],
    region: account.data_center === 'eu' ? Region.EU : Region.US,
    support_tier: account.support_tier,
    suspend_trial_at: account.suspend_trial_at,
    account_state: account.account_state,
    access_granted_until: account.access_granted_until,
    access_granted_by: account.access_granted_by,
    access_granted_on: account.access_granted_on,
    data_center: account.data_center,
    has_csm: account.has_csm,
    cs_access_granted_until: account.cs_access_granted_until,
    cs_access_granted_by: account.cs_access_granted_by,
    cs_access_granted_on: account.cs_access_granted_on,
    ordway_id: account.ordway_id,
    read_only: account.read_only,
    verification_state: account.verification_state,
  };
}

// TODO: would be great if someone cleaned this up someday
function getUserFromRaw(account: RawAccount, user: RawUser): User {
  return {
    id: +user.id,
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    account_id: +account.id,
    last_cio_workspace_id: user.last_environment_id,
    account_permissions: user.account_permissions,
    roles: user.roles ?? [],
    masq_permissions: user.masq_permissions ?? UserMasqPermission.NoMasqAccess,
    can_masquerade: user.can_masquerade ?? false,
    can_masquerade_write: user.can_masquerade_write ?? false,
    user_flags: user.user_flags,
  };
}
